import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { OrgResponse } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { StatusBadge } from "@/components/StatusBadge";
import { orgLegalName } from "@/shared/orgDisplay";

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function OrgDetailHeader({ org }: { org: OrgResponse }) {
  const owner =
    org.owner_display_name ?? org.owner_full_name ?? "Owner unavailable";
  const reviewer = org.reviewed_by_display_name ?? "Reviewer unavailable";

  return (
    <Paper
      component="section"
      aria-label="Organization profile"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { sm: "flex-start" },
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography color="text.secondary" variant="caption">
              Legal name
            </Typography>
            <Typography sx={{ overflowWrap: "anywhere" }}>
              {orgLegalName(org) ?? "Legal name not provided"}
            </Typography>
          </Box>
          <StatusBadge status={org.status} />
        </Stack>

        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            m: 0,
          }}
        >
          <Fact label="Owner" value={owner} />
          <Fact label="Tax ID" value={org.tax_id ?? "Not provided"} />
          <Fact label="Created" value={formatDate(org.created_at)} />
          {(org.reviewed_by || org.reviewed_by_display_name) && (
            <Fact label="Reviewed by" value={reviewer} />
          )}
          {org.reviewed_at && (
            <Fact label="Reviewed at" value={formatDate(org.reviewed_at)} />
          )}
        </Box>

        {org.status === "rejected" && org.rejection_reason && (
          <Alert severity="error">
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Rejection reason
              </Typography>
              <Typography variant="body2">{org.rejection_reason}</Typography>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
